import React from 'react';
import CrashFallback from './CrashFallback';

/**
 * The app's only real error boundary.
 *
 * Before this, `grep -rn "componentDidCatch" src/` matched exactly one thing:
 * `LexicalErrorBoundary`, third-party, scoped to the rich-text editor. React
 * unmounts the entire tree when a render/lifecycle error reaches the root
 * uncaught, so any unhandled render error anywhere in the app produced a
 * blank `#root` — on the desktop build that is a white window with no title
 * bar, no menu and no way to get back, since the whole chrome is React too.
 *
 * WHAT A BOUNDARY CANNOT DO, so nobody assumes otherwise: it catches errors
 * thrown during render, in lifecycle methods, and in constructors of the tree
 * below it. It does NOT catch event handlers, async callbacks, timers, or
 * anything thrown outside React's rendering — including a rejected
 * `bootstrap()`, which happens before this component exists at all. index.js
 * handles that case with its own catch.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Logged, not reported: reportService needs an authenticated session and
    // a filled-in form (see CrashFallback's note). In the desktop build this
    // reaches the devtools console and the webview log, which is what a bug
    // report will be asked for anyway.
    console.error('[app] render error:', error, info?.componentStack);
    if (typeof this.props.onError === 'function') {
      try {
        this.props.onError(error, info);
      } catch {
        // A failing error handler must not replace the error being handled.
      }
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <CrashFallback
        title="Apiboo hit an unexpected error"
        message="The app stopped rendering. Reloading usually clears it — your workspaces, collections and documents are unaffected."
        detail={formatError(error)}
      />
    );
  }
}

/**
 * Errors are not always Errors. Tauri command rejections are plain strings,
 * and a thrown object shows as "[object Object]" unless it is unpacked —
 * which is exactly the detail a bug report needs.
 */
function formatError(error) {
  if (typeof error === 'string') return error;
  const name = error?.name || 'Error';
  const message = error?.message || String(error);
  const stack = typeof error?.stack === 'string' ? error.stack : '';
  return stack || `${name}: ${message}`;
}
